import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getAvailablePackageServicesRequest } from "services/modules/events/packages";
import { ClearAvailablePackageServices } from "services/modules/events/packages/Action";

const KIND_OPTIONS = ["mandatory", "included", "additional"];

const kindChipSx = {
  mandatory: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    color: "#C62828",
  },
  included: {
    backgroundColor: "rgba(33, 150, 243, 0.1)",
    color: "#1565C0",
  },
  additional: {
    backgroundColor: "rgba(255, 152, 0, 0.12)",
    color: "#EF6C00",
  },
};

const buildSelectionFromServices = (services) => {
  const selected = [];
  (services || []).forEach((service) => {
    (service?.options || []).forEach((opt) => {
      if (!opt?.in_package || !opt?.variant_id) return;
      selected.push({
        variant_id: opt.variant_id,
        kind: opt.kind || "included",
        quantity: opt.quantity ?? 1,
        display_priority: service?.display_priority ?? 0,
        product_id: service?.product_id,
        name: service?.name,
        option: opt.option,
        image: opt.image || service?.image,
      });
    });
  });
  return selected;
};

const PackageServicesForm = ({
  eventTypeId,
  packageId,
  value = [],
  onChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const services = useSelector((state) => state.packages.availableServices);
  const load = useSelector((state) => state.packages.load);
  const lastEventTypeRef = useRef(null);
  const hydratedPackageRef = useRef(null);

  const selectedMap = useMemo(() => {
    const map = new Map();
    (value || []).forEach((item) => {
      if (item?.variant_id != null) {
        map.set(Number(item.variant_id), item);
      }
    });
    return map;
  }, [value]);

  useEffect(() => {
    if (!eventTypeId) {
      lastEventTypeRef.current = null;
      hydratedPackageRef.current = null;
      dispatch(ClearAvailablePackageServices());
      onChange?.([]);
      return;
    }

    if (lastEventTypeRef.current !== eventTypeId) {
      const switching =
        lastEventTypeRef.current != null &&
        lastEventTypeRef.current !== eventTypeId;
      lastEventTypeRef.current = eventTypeId;
      if (switching) {
        hydratedPackageRef.current = null;
        onChange?.([]);
      }
    }

    const params = { event_type_id: eventTypeId };
    if (packageId) params.package_id = packageId;

    dispatch(
      getAvailablePackageServicesRequest({
        params,
        action: (res) => {
          if (packageId && hydratedPackageRef.current !== String(packageId)) {
            hydratedPackageRef.current = String(packageId);
            onChange?.(buildSelectionFromServices(res?.data?.services || []));
          }
        },
      }),
    );
  }, [eventTypeId, packageId]);

  const updateItem = (variantId, patch) => {
    const next = [...(value || [])];
    const idx = next.findIndex(
      (i) => Number(i.variant_id) === Number(variantId),
    );
    if (idx === -1) return;
    next[idx] = { ...next[idx], ...patch };
    onChange?.(next);
  };

  const toggleOption = (service, opt, checked) => {
    const variantId = Number(opt.variant_id);
    if (checked) {
      onChange?.([
        ...(value || []).filter((i) => Number(i.variant_id) !== variantId),
        {
          variant_id: variantId,
          kind: "included",
          quantity: 1,
          display_priority: service?.display_priority ?? 0,
          product_id: service?.product_id,
          name: service?.name,
          option: opt.option,
          image: opt.image || service?.image,
        },
      ]);
    } else {
      onChange?.(
        (value || []).filter((i) => Number(i.variant_id) !== variantId),
      );
    }
  };

  const isLoading = load?.loading && load?.type === "available-services";

  if (!eventTypeId) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mt: 2,
          border: "1px solid #E8E8E8",
          borderRadius: "10px",
          width: "100%",
        }}
      >
        <Typography fontSize="18px" fontWeight="600" mb={1}>
          {t("events_packages.services_title")}
        </Typography>
        <Typography fontSize="14px" color="#8A8A8A">
          {t("events_packages.select_event_type_first")}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mt: 2,
        border: "1px solid #E8E8E8",
        borderRadius: "10px",
        width: "100%",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={1}
        mb={2.5}
      >
        <Box>
          <Typography fontSize="18px" fontWeight="600">
            {t("events_packages.services_title")}
          </Typography>
          <Typography fontSize="13px" color="#797979">
            {(value || []).length} {t("events_packages.items_count_label")}
          </Typography>
        </Box>
      </Stack>

      {isLoading ? (
        <Typography color="#8A8A8A">
          {t("events_packages.services_loading")}
        </Typography>
      ) : !services?.length ? (
        <Box
          sx={{
            py: 4,
            textAlign: "center",
            borderRadius: "8px",
            backgroundColor: "#FAFAFA",
            border: "1px solid #F0F0F0",
          }}
        >
          <Typography color="#8A8A8A">
            {t("events_packages.services_empty")}
          </Typography>
        </Box>
      ) : (
        <Stack gap={2}>
          {services.map((service) => (
            <Paper
              key={service.product_id}
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid #E8E8E8",
                borderRadius: "10px",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                gap={2}
                alignItems={{ xs: "flex-start", sm: "flex-start" }}
                mb={1.5}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 60,
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "#F5F5F5",
                    border: "1px solid #EEEEEE",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {service?.image ? (
                    <img
                      src={service.image}
                      alt={service?.name || ""}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Typography fontSize="11px" color="#A0A0A0">
                      {t("chat.no_image")}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography fontSize="16px" fontWeight="600">
                    {service?.name || "-"}
                  </Typography>
                  <Stack direction="row" gap={0.75} flexWrap="wrap" mt={0.75}>
                    {service?.category?.name && (
                      <Chip
                        size="small"
                        label={service.category.name}
                        sx={{
                          height: 22,
                          fontSize: "12px",
                          backgroundColor: "rgba(33, 150, 243, 0.08)",
                          color: "#1565C0",
                        }}
                      />
                    )}
                    <Chip
                      size="small"
                      label={`${t("events_packages.display_priority")}: ${
                        service?.display_priority ?? 0
                      }`}
                      sx={{ height: 22, fontSize: "12px" }}
                    />
                  </Stack>
                </Box>
              </Stack>

              <Stack gap={1.25}>
                {(service?.options || []).map((opt) => {
                  const selected = selectedMap.get(Number(opt.variant_id));
                  const checked = !!selected;

                  return (
                    <Box
                      key={opt.variant_id}
                      sx={{
                        p: 1.5,
                        borderRadius: "8px",
                        backgroundColor: checked ? "#F7FAF7" : "#FAFAFA",
                        border: checked
                          ? "1px solid #C8E6C9"
                          : "1px solid #EEEEEE",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        gap={1.5}
                        alignItems={{ xs: "stretch", md: "center" }}
                      >
                        <FormControlLabel
                          sx={{ minWidth: { md: 220 }, m: 0, flex: 1 }}
                          control={
                            <Checkbox
                              checked={checked}
                              onChange={(e) =>
                                toggleOption(service, opt, e.target.checked)
                              }
                            />
                          }
                          label={
                            <Box>
                              <Typography fontSize="14px" fontWeight={500}>
                                {opt?.option ||
                                  t("events_packages.no_option")}
                              </Typography>
                              <Typography fontSize="12px" color="#6A6A6A">
                                {opt?.price_after_discount || opt?.price}{" "}
                                {t("products.curr_sar")}
                              </Typography>
                            </Box>
                          }
                        />

                        {checked && (
                          <>
                            <TextField
                              select
                              size="small"
                              label={t("events_packages.kind")}
                              value={selected?.kind || "included"}
                              onChange={(e) =>
                                updateItem(opt.variant_id, {
                                  kind: e.target.value,
                                })
                              }
                              sx={{ minWidth: 140, backgroundColor: "#fff" }}
                            >
                              {KIND_OPTIONS.map((k) => (
                                <MenuItem key={k} value={k}>
                                  {t(`events_packages.kinds.${k}`)}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              size="small"
                              type="number"
                              label={t("events_packages.quantity")}
                              value={selected?.quantity ?? 1}
                              onChange={(e) =>
                                updateItem(opt.variant_id, {
                                  quantity: e.target.value,
                                })
                              }
                              sx={{
                                width: { xs: "100%", md: 110 },
                                backgroundColor: "#fff",
                              }}
                              inputProps={{ min: 1 }}
                            />
                            <TextField
                              size="small"
                              type="number"
                              label={t("events_packages.display_priority")}
                              value={selected?.display_priority ?? 0}
                              onChange={(e) =>
                                updateItem(opt.variant_id, {
                                  display_priority: e.target.value,
                                })
                              }
                              sx={{
                                width: { xs: "100%", md: 130 },
                                backgroundColor: "#fff",
                              }}
                            />
                            <Chip
                              size="small"
                              label={t(
                                `events_packages.kinds.${
                                  selected?.kind || "included"
                                }`,
                              )}
                              sx={{
                                fontWeight: 600,
                                ...(kindChipSx[selected?.kind] || {}),
                              }}
                            />
                          </>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default PackageServicesForm;
